import { LoaderCircle } from "lucide-react";
import { QuickActionPlacement } from "~/services/quickActions";
import { QuickActionIcon } from "./QuickActionIcon";
import { useInvokeQuickAction } from "./useInvokeQuickAction";
import { useQuickActions } from "./useQuickActions";

export interface QuickActionsGridProps {
  placement: QuickActionPlacement;
  placementId?: string;
}

/** Home's "Quick actions" section: a 3-column grid of suggestion cards. */
export const QuickActionsGrid = ({ placement, placementId }: QuickActionsGridProps) => {
  const { actions, isLoading } = useQuickActions(placement, placementId);
  const { invoke, invokingId } = useInvokeQuickAction();

  if (isLoading || actions.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3" aria-labelledby="home-quick-actions-heading">
      <h2 id="home-quick-actions-heading" className="text-[17px] font-semibold text-foreground">
        Quick actions
      </h2>
      <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
        {actions.map((action) => (
          <button
            key={action.id}
            type="button"
            disabled={invokingId === action.id}
            onClick={() => invoke(action.id)}
            className="flex cursor-pointer items-center gap-3.5 rounded-xl border border-border p-4.5 text-left transition-colors hover:border-primary/40 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex size-11 shrink-0 items-center justify-center rounded-(--mds-radius-lg) bg-primary/10 text-primary dark:bg-primary/20">
              {invokingId === action.id ? (
                <LoaderCircle className="size-6 animate-spin" aria-hidden="true" />
              ) : (
                <QuickActionIcon name={action.icon} className="size-6" />
              )}
            </span>
            <span className="min-w-0">
              <span className="block text-base font-bold tracking-tight text-foreground">
                {action.title}
              </span>
              <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                {action.subtitle}
              </span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
};
